/**
 * Barkod okuyucu dinleyicisi (keyboard-wedge).
 *
 * El terminallerindeki okuyucular ve USB/bluetooth okuyucular kodu klavyeden
 * yazılmış gibi gönderir ve sonuna Enter ekler. Odak nerede olursa olsun
 * yakalanır — operatörün bir alana tıklaması gerekmez.
 *
 * Karakterler arası hız kontrolü YAPILMAZ. Önceki sürümde 35 ms'lik bir eşik
 * vardı ve karakterler daha yavaş geldiğinde tampon her seferinde sıfırlanıp
 * okuma tamamen kayboluyordu (Honeywell EDA61'de yaşandı). Referans Flutter
 * uygulaması da yalnızca Enter'a kadar biriktiriyor (lazerokuyucu.dart:44).
 *
 * İnsan yazımıyla karışma riski yok: metin alanları `data-manual-input`
 * ile işaretli ve orada dinleyici devre dışı kalıyor.
 */

export interface ScannerOptions {
  /** Tam bir barkod okunduğunda çağrılır. */
  onScan: (code: string) => void;
  /** Bundan kısa diziler yok sayılır (tek tuş basımları). */
  minLength?: number;
  /**
   * Yarım kalmış okuma bu süre sonra atılır. Okuyucu bir barkodu saniyeler
   * içinde gönderir; bundan uzun sessizlik, okumanın yarıda kaldığını gösterir.
   */
  timeoutMs?: number;
  /** Tanı için: her tuş sonrası tamponun durumu. */
  onBuffer?: (buffer: string) => void;
}

export function startScannerListener(options: ScannerOptions): () => void {
  const { onScan, minLength = 3, timeoutMs = 3000, onBuffer } = options;

  let buffer = '';
  let lastKeyAt = 0;

  function onKeyDown(e: KeyboardEvent) {
    // Kullanıcı bir metin alanına elle yazıyorsa karışma.
    const target = e.target as HTMLElement | null;
    if (target?.dataset.manualInput === 'true') return;

    const now = performance.now();

    // Uzun sessizlikten sonra gelen tuş yeni bir okumanın başlangıcıdır.
    if (buffer && now - lastKeyAt > timeoutMs) buffer = '';
    lastKeyAt = now;

    if (e.key === 'Enter') {
      const code = buffer.trim();
      buffer = '';
      onBuffer?.('');

      if (code.length >= minLength) {
        e.preventDefault();
        onScan(code);
      }
      return;
    }

    // Tek karakterlik tuşlar dışındakiler (Shift, Tab, F1…) yok sayılır.
    if (e.key.length !== 1) return;

    buffer += e.key;
    onBuffer?.(buffer);
  }

  window.addEventListener('keydown', onKeyDown, true);
  return () => window.removeEventListener('keydown', onKeyDown, true);
}
